import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { documentRequest, generateDocuments, getJobStatus } from "../api";
import {
  Container,
  Title,
  Textarea,
  Button,
  Stack,
  TextInput,
  Paper,
  Group,
  Text,
  Box,
  Checkbox,
  Grid,
  UnstyledButton,
  Loader,
  Flex,
  Avatar,
  Modal,
  Accordion,
  Badge,
  Divider,
  Progress,
  Transition,
  Collapse,
  Chip,
} from "@mantine/core";
import {
  IconDownload,
  IconRefresh,
  IconSend,
  IconPaperclip,
  IconUpload,
  IconTrash,
  IconFileText,
  IconEye,
} from "@tabler/icons-react";
import { useNotification } from "../hooks/useNotification";
import { NOTIFICATION_MESSAGES } from "../constants/notificationMessages";
import { useAuthCheck } from "../hooks/useAuthCheck";
import { ATTACHMENTS } from "../constants/attachmentContents";
import { ASSOCIATE_EMAIL } from "../constants/emailConstants.js";

function EmailDemo() {
  const { isAuthenticated, isLoading } = useAuthCheck();
  const navigate = useNavigate();
  const [emailBody, setEmailBody] = useState("");
  const [fieldInputs, setFieldInputs] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [inputError, setInputError] = useState(false);
  const [templateName, setTemplateName] = useState(null);

  const timerRef = useRef(null);
  const { showNotification, updateNotification } = useNotification();
  const [activeAccordion, setActiveAccordion] = useState(["step1"]);
  const [attachmentProgress, setAttachmentProgress] = useState({});
  const [formEdited, setFormEdited] = useState(false);
  const [emailTransitions, setEmailTransitions] = useState({
    step1: true,
    step2: false,
    step3: false,
  });
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [selectedAttachment, setSelectedAttachment] = useState([]);
  const [attachment, setAttachment] = useState(null);
  const [documentSets, setDocumentSets] = useState([]);

  const presets = {
    safe: `VC Demo Partners L.P. decided to join the SAFE round! 
    
They are coming in for $500k on the $20 million valuation cap form.  They want an MFN.

Can you prepare a SAFE and a side letter?
    
I will be signing for them, and Tee Jefferson will be signing for them as Managing Partner.  

Thanks,

Giles Corey
Co-Founder
Socius Demo Startup, Inc.
123 Appian Way
New York, NY 12345`,
    //     safeSideLetter: `We're finalizing a SAFE investment with VC Demo Partners L.P. Can you prepare a standard YC side letter?

    // Please date it as of today. We need this ASAP to close the round. Let me know if you need any other information.

    // Thanks,

    // Giles Corey
    // Co-Founder
    // Socius Demo Startup, Inc.
    // 123 Appian Way
    // New York, NY 12345`,
    safeBoardConsent: ` We need to prepare a Board Consent for Socius Demo Startup's SAFE financing.

We're issuing multiple SAFEs to several investors for $2.5m total. Please include standard language for pro-rata rights waiver.

The two founders (Giles Corey and John Proctor) are the only directors.

Can you draft this up for us today? They want to close by tomorrow.

Thanks,

Katherine (Kate) Kessel
Partner
Kenobi, Windu & Organa LLP

One Republic Plaza, Suite 3187
515 South Corellia Blvd.
New York, NY 12345
Phone: (213) 555-0187
Email: k.kessel@kenobilaw.com

`,
    //     ycTermSheet: `VC Demo Partners agreed to lead our Series Seed! They asked us to draft a term sheet on the following terms:

    // Total funding: $500k
    // Valuation: $5 million post-money
    // Investors: Demo VC Partners L.P. and two angel investors (names TBD)

    // They didn't budget on vesting, so we're looking at a standard 4-year vesting schedule with a 1-year cliff for the founders. Also, please make sure to include the usual pro-rata rights.

    // Can you draft this up for us? We're aiming to close by the end of next week.

    // Thanks,

    // Giles Corey
    // Co-Founder
    // Socius Demo Startup, Inc.
    // 123 Appian Way
    // New York, NY 12345`,
    nvcaTermSheet: `We decided to go forward with the investment in Socius Demo Startup. Can you please prepare a term sheet based on the NVCA form? Here are some key details:

Financing: Series A
Total Financing Amount: $8,000,000
Pre-Money Valuation: $32,000,000

Some additional terms we'd like to include:

1. Multiple closings allowed within 90 days
2. 10% option pool
3. 1x non-participating liquidation preference
4. Broad-based weighted average anti-dilution
5. Investor director seat for Quantum Ventures
6. Information rights and pro-rata rights for major investors (defined as $500k+)
7. 180-day lock-up period post-IPO

We're aiming for standard terms in most areas, but please let me know if you need any clarification or additional information.

Thanks in advance for your help!

Thanks,

Thomas "Tee" Jefferson Cavalier III
Managing Partner
VC Demo Partners L.P.

1819 Lawn Drive, Suite 1825
Charlottesville, VA 22903
Phone: (434) 555-0142
Email: tjc3@vcdemopartners.com`,
    nvcaVA: {
      content: `Can you please draft a Voting Agreement for our Series Seed financing? The term sheet is attached.

Thanks,
Giles Corey
President, Socius Demo Startup, Inc.`,
      attachments: [
        "SOCIUS_DEMO_STARTUP_INC_SEED_TERM_SHEET",
        "SOCIUS_DEMO_CONVERTIBLES_CORP_SEED_TERM_SHEET",
      ],
    },
    nvcaSPA: {
      content: `Can you please draft a Stock Purchase Agreement for our Series Seed round? The term sheet is attached.

Thanks,
Giles Corey
President, Socius Demo Startup, Inc.`,
      attachments: [
        "SOCIUS_DEMO_STARTUP_INC_SEED_TERM_SHEET",
        "SOCIUS_DEMO_CONVERTIBLES_CORP_SEED_TERM_SHEET",
      ],
    },
    nvcaIRA: {
      content: `Can you please draft an Investor Rights Agreement for our Series Seed financing? The term sheet is attached.

Thanks,
Giles Corey
President, Socius Demo Startup, Inc.`,
      attachments: [
        "SOCIUS_DEMO_STARTUP_INC_SEED_TERM_SHEET",
        "SOCIUS_DEMO_CONVERTIBLES_CORP_SEED_TERM_SHEET",
      ],
    },
    nvcaROFR: {
      content: `Can you please draft a Right of First Refusal and Co-Sale Agreement for our Series Seed round? The term sheet is attached.

Thanks,
Giles Corey
President, Socius Demo Startup, Inc.`,
      attachments: [
        "SOCIUS_DEMO_STARTUP_INC_SEED_TERM_SHEET",
        "SOCIUS_DEMO_CONVERTIBLES_CORP_SEED_TERM_SHEET",
      ],
    },
    nvcaCHARTER: {
      content: `Can you please update our corporate charter in preparation for our Series Seed financing? The term sheet is attached.

Thanks,
Giles Corey
President, Socius Demo Startup, Inc.`,
      attachments: [
        "SOCIUS_DEMO_STARTUP_INC_SEED_TERM_SHEET",
        "SOCIUS_DEMO_CONVERTIBLES_CORP_SEED_TERM_SHEET",
      ],
    },
    nvcaPrimaryDocuments: {
      content: `Can you please draft a full set of the NVCA primary documents for our Series Seed financing? The term sheet is attached.

Thanks,
Giles Corey
President, Socius Demo Startup, Inc.`,
      attachments: [
        "SOCIUS_DEMO_STARTUP_INC_SEED_TERM_SHEET",
        "SOCIUS_DEMO_CONVERTIBLES_CORP_SEED_TERM_SHEET",
      ],
    },
  };

  const formatFileName = (fileName) => {
    return fileName
      .replace(/_/g, " ") // Replace underscores with spaces
      .replace(/\.[^/.]+$/, "") // Remove file extension
      .split(" ") // Split into words
      .map((word) => {
        // Capitalize each word and add a period after "Inc" or "Corp"
        const capitalizedWord =
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        return ["Inc", "Corp"].includes(capitalizedWord)
          ? `${capitalizedWord}.`
          : capitalizedWord;
      })
      .join(" "); // Join words back into a string
  };

  const handlePresetSelect = (preset) => {
    if (selectedPreset === preset) {
      // unselect
      setSelectedPreset(null);
      setEmailBody("");
      setAttachment(null);
      setSelectedAttachment([]);
    } else {
      setSelectedPreset(preset);
      const presetData = presets[preset];

      // handle preset attachments if they exist
      if (typeof presetData === "object" && presetData.attachments) {
        const defaultAttachmentId = presetData.attachments[0];
        const defaultAttachment = ATTACHMENTS[defaultAttachmentId];
        setAttachment({
          id: defaultAttachmentId,
          name: defaultAttachment.name,
          content: defaultAttachment.content,
        });
        setSelectedAttachment([defaultAttachmentId]);
        setEmailBody(presetData.content);
      } else {
        setEmailBody(presetData);
        setAttachment(null);
        setSelectedAttachment([]);
      }
    }
  };

  const handleAttachmentSelect = (attachmentId) => {
    const presetData = presets[selectedPreset];

    if (!presetData?.attachments) return;

    // If clicking the same attachment, deselect it
    if (selectedAttachment.includes(attachmentId)) {
      setSelectedAttachment([]);
      setAttachment(null);
    } else {
      // Select only the new attachment
      setSelectedAttachment([attachmentId]);
      const primaryAttachment = ATTACHMENTS[attachmentId];
      setAttachment({
        id: attachmentId,
        name: primaryAttachment.name,
        content: primaryAttachment.content,
      });
    }
  };

  const renderAttachmentOptions = (attachments) => (
    <Box mt="md">
      <Text size="xs" fw={500} mb="xs">
        Available Attachments:
      </Text>
      <Group>
        {attachments.map((attachmentId, index) => (
          <Chip
            key={index}
            checked={selectedAttachment.includes(attachmentId)}
            onChange={() => handleAttachmentSelect(attachmentId)}
            size="xs"
            variant="light"
          >
            {ATTACHMENTS[attachmentId].name}
          </Chip>
        ))}
      </Group>
    </Box>
  );

  const renderEmailContent = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            <Stack gap="xs">
              <Text size="xs" c="dimmed">
                Use one of the presets below to get started, feel free to edit
                it before requesting!
              </Text>
              <Group gap="xs">
                {Object.entries(presets).map(([key, _]) => (
                  <Chip
                    key={key}
                    checked={selectedPreset === key}
                    onChange={() => handlePresetSelect(key)}
                    size="xs"
                    radius="lg"
                    variant="light"
                  >
                    {key === "safe"
                      ? "SAFE w/ Side Letter"
                      : key === "safeSideLetter"
                        ? "SAFE Side Letter"
                        : key === "safeBoardConsent"
                          ? "SAFE Board Consent"
                          : key === "ycTermSheet"
                            ? "Term Sheet (YC)"
                            : key === "nvcaTermSheet"
                              ? "Term Sheet"
                              : key === "nvcaVA"
                                ? "Voting Agreement (NVCA)"
                                : key === "nvcaSPA"
                                  ? "Stock Purchase Agreement (NVCA)"
                                  : key === "nvcaIRA"
                                    ? "Investor Rights Agreement (NVCA)"
                                    : key === "nvcaROFR"
                                      ? "Right of First Refusal (NVCA)"
                                      : key === "nvcaCHARTER"
                                        ? "Charter (NVCA)"
                                        : key === "nvcaPrimaryDocuments"
                                          ? "Primary Documents (NVCA)"
                                          : key}
                  </Chip>
                ))}
              </Group>
            </Stack>
            <Textarea
              value={emailBody}
              onChange={(e) => {
                setEmailBody(e.target.value);
                setInputError(false);
              }}
              placeholder="Dear Legal Team,

I hope this email finds you well. We need to prepare a SAFE for Ultimate Fund's upcoming investment round into ABC Corp. 

Here are some details:
- They are looking to invest 100k at a $28m Valuation Cap
- We are going to need an MFN (Most Favored Nation) clause
- They need a 10% discount rate.

Please draft the SAFE agreement based on these terms, let me know if you need any additional information.

Thanks,
Socius User"
              autosize
              minRows={10}
              error={inputError}
            />
            {selectedPreset && presets[selectedPreset]?.attachments
              ? renderAttachmentOptions(presets[selectedPreset].attachments)
              : null}
          </>
        );
      case 1:
        return (
          <>
            <Text size="sm">Hey,</Text>
            <Text size="sm">
              I've attached an initial draft based on what I gathered from your
              mail.
            </Text>
            <Text size="sm" mt="md">
              If you have any changes to send over, I will get a final draft out
              to you ASAP.
            </Text>
            <Text size="sm">Best regards,</Text>
            <Text size="sm">AI Legal Assistant</Text>
            {renderAttachmentSection()}
          </>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (currentStep === 1) {
      setActiveAccordion((prev) => [...prev, "step2"]);
    } else if (currentStep === 2) {
      setActiveAccordion((prev) => [...prev, "step3"]);
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStep >= 1) {
      ["output_docx_url", "redline_url"].forEach(simulateAttachmentDownload);
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStep >= 1) {
      setTimeout(
        () => setEmailTransitions((prev) => ({ ...prev, step2: true })),
        300,
      );
    }
    if (currentStep >= 2) {
      setTimeout(
        () => setEmailTransitions((prev) => ({ ...prev, step3: true })),
        300,
      );
    }
  }, [currentStep]);

  const simulateAttachmentDownload = (docSet, type) => {
    const key = `${docSet.fileId}_${type}`;
    let progress = 0;

    const interval = setInterval(() => {
      progress += Math.random() * 5;
      if (progress > 95) {
        progress = 95; // Cap at 95% until job is complete
      }
      setAttachmentProgress((prev) => ({ ...prev, [key]: progress }));
    }, 200);

    return () => clearInterval(interval);
  };

  const formatEmailWithAttachments = (emailBody, attachments = []) => {
    let formattedEmail = "";

    if (attachments.length > 0) {
      formattedEmail += "<attachments>\n";
      attachments.forEach((attachment) => {
        formattedEmail += `<attachment name="${attachment.name}">${attachment.content}</attachment>\n`;
      });
      formattedEmail += "</attachments>\n";
    }

    formattedEmail += `<email>${emailBody}</email>`;
    return formattedEmail;
  };

  const handleInitialEmail = async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setInputError(false);

    const notificationId = showNotification(
      NOTIFICATION_MESSAGES.EMAIL_GENERATOR.LOADING.title,
      NOTIFICATION_MESSAGES.EMAIL_GENERATOR.LOADING.message,
    );
    let cleanupFns = [];

    try {
      // Format email with attachments if they exist
      const formattedEmail = formatEmailWithAttachments(
        emailBody,
        selectedAttachment.map((attachmentId) => ATTACHMENTS[attachmentId]),
      );

      // Use the formatted email in your API call
      const parseResult = await documentRequest(formattedEmail);

      pollJobStatus(
        parseResult.trace_id,
        async (prepareResult) => {
          if (
            !prepareResult.templates ||
            prepareResult.templates.length === 0
          ) {
            updateNotification(
              notificationId,
              NOTIFICATION_MESSAGES.EMAIL_GENERATOR.ERROR.title,
              "Couldn't determine which type of document to prepare. Please provide more detail.",
              "error",
            );
            setInputError(true);
            setLoading(false);
            setTemplateName(prepareResult.templates[0].template_friendly_name);
            return;
          }

          // Set template name from the first response
          setTemplateName(prepareResult.templates[0].friendly_name);

          // Create initial document sets
          const initialDocumentSets = prepareResult.templates.map(
            (template) => ({
              templateName: template.name,
              templateFriendlyName: template.friendly_name, // This should now always exist
              fileId: template.id,
              urls: {},
            }),
          );
          setDocumentSets(initialDocumentSets);

          // Move to step 1 and start progress simulation
          setCurrentStep(1);
          setEmailTransitions((prev) => ({ ...prev, step2: true }));
          setActiveAccordion((prev) => [...prev, "step2"]);

          try {
            const generateResult = await generateDocuments({
              schema_name: prepareResult.schema_name,
              templates: prepareResult.templates,
            });

            // Start progress simulation for each document
            cleanupFns = prepareResult.templates
              .map((template) => {
                return ["docx", "redline"].map((type) =>
                  simulateAttachmentDownload({ fileId: template.id }, type),
                );
              })
              .flat();

            pollJobStatus(
              generateResult.trace_id,
              (docResults) => {
                if (docResults.documents) {
                  // Update existing document sets with URLs
                  const updatedDocumentSets = docResults.documents.map(
                    (doc) => ({
                      templateName: doc.template_name,
                      templateFriendlyName: doc.template_friendly_name,
                      fileId: doc.file_id,
                      urls: doc.files,
                    }),
                  );
                  setDocumentSets(updatedDocumentSets);

                  updateNotification(
                    notificationId,
                    NOTIFICATION_MESSAGES.EMAIL_GENERATOR.SUCCESS.title,
                    NOTIFICATION_MESSAGES.EMAIL_GENERATOR.SUCCESS.message,
                    "success",
                  );
                  setLoading(false);

                  // Cleanup progress simulation
                  cleanupFns.forEach((cleanup) => cleanup());
                }
              },
              (error) => {
                console.error("Document generation error:", error);
                updateNotification(
                  notificationId,
                  NOTIFICATION_MESSAGES.EMAIL_GENERATOR.ERROR.title,
                  error.message ||
                    NOTIFICATION_MESSAGES.EMAIL_GENERATOR.ERROR.message,
                  "error",
                );
                setInputError(true);
                setLoading(false);

                // Cleanup progress simulation
                cleanupFns.forEach((cleanup) => cleanup());
              },
            );
          } catch (error) {
            updateNotification(
              notificationId,
              NOTIFICATION_MESSAGES.EMAIL_GENERATOR.ERROR.title,
              error.message ||
                NOTIFICATION_MESSAGES.EMAIL_GENERATOR.ERROR.message,
              "error",
            );
            setInputError(true);
            setLoading(false);

            // Cleanup progress simulation
            cleanupFns.forEach((cleanup) => cleanup());
          }
        },
        (error) => {
          updateNotification(
            notificationId,
            NOTIFICATION_MESSAGES.EMAIL_GENERATOR.ERROR.title,
            error.message ||
              NOTIFICATION_MESSAGES.EMAIL_GENERATOR.ERROR.message,
            "error",
          );
          setInputError(true);
          setLoading(false);

          // Cleanup progress simulation
          cleanupFns.forEach((cleanup) => cleanup());
        },
      );
    } catch (error) {
      console.error("Error preparing document:", error);
      updateNotification(
        notificationId,
        NOTIFICATION_MESSAGES.EMAIL_GENERATOR.ERROR.title,
        error.message || NOTIFICATION_MESSAGES.EMAIL_GENERATOR.ERROR.message,
        "error",
      );
      setInputError(true);
      setLoading(false);

      // Cleanup progress simulation
      cleanupFns.forEach((cleanup) => cleanup());
    }
  };

  const formatTitleCase = (str) => {
    return str
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.getMonth() + 1}.${d.getDate()}.${d.getFullYear().toString().slice(2)}`;
  };

  const getDocumentFileName = (url, type) => {
    const baseFileName = fieldInputs?.template_name || "document";
    const titleCaseName = formatTitleCase(baseFileName);
    const today = formatDate(new Date());

    switch (type) {
      case "docx":
      case "redline":
        return `${titleCaseName} [v Form].pdf`;
      default:
        // Extract and format filename from URL for other cases
        const urlFileName = url.split("/").pop().split(".")[0];
        return `${formatTitleCase(urlFileName)} [${today}].${url.split(".").pop()}`;
    }
  };

  const handleDownload = (url, type) => {
    const link = document.createElement("a");
    link.href = url;
    const fileName = getDocumentFileName(url, type);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderAttachmentSection = () => (
    <Box mt="md">
      {documentSets && documentSets.length > 0 && (
        <>
          <Text size="xs" fw={500} mb="xs">
            Attachments:
          </Text>
          {documentSets.map((docSet, index) => (
            <Box key={index} mb="sm">
              <Group>
                <div>
                  <Button
                    size="xs"
                    onClick={() =>
                      handleDownload(docSet.urls?.output_docx_url, "docx")
                    }
                    leftSection={<IconDownload size={18} />}
                    disabled={
                      !docSet.urls?.output_docx_url ||
                      attachmentProgress[`${docSet.fileId}_docx`] < 100
                    }
                    variant="light"
                    color="gray"
                  >
                    {docSet.templateFriendlyName || templateName}.docx
                  </Button>
                  {attachmentProgress[`${docSet.fileId}_docx`] < 100 && (
                    <Progress
                      value={attachmentProgress[`${docSet.fileId}_docx`] || 0}
                      size="xs"
                      mt="xs"
                      animated
                    />
                  )}
                </div>
                <div>
                  <Button
                    size="xs"
                    onClick={() =>
                      handleDownload(docSet.urls?.redline_url, "redline")
                    }
                    leftSection={<IconDownload size={18} />}
                    disabled={
                      !docSet.urls?.redline_url ||
                      attachmentProgress[`${docSet.fileId}_redline`] < 100
                    }
                    variant="light"
                    color="gray"
                  >
                    {docSet.templateFriendlyName || templateName} Redline [v
                    Form].pdf
                  </Button>
                  {attachmentProgress[`${docSet.fileId}_redline`] < 100 && (
                    <Progress
                      value={
                        attachmentProgress[`${docSet.fileId}_redline`] || 0
                      }
                      size="xs"
                      mt="xs"
                      animated
                    />
                  )}
                </div>
              </Group>
            </Box>
          ))}
        </>
      )}
    </Box>
  );

  if (isLoading) {
    return (
      <Container size="xl">
        <Box
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <Loader type="dots" size="xl" />
        </Box>
      </Container>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const resetState = () => {
    setEmailBody("");
    setFieldInputs(null);
    setLoading(false);
    setCurrentStep(0);
    setInputError(false);
    setSelectedPreset(null);
    setAttachment(null);
    setSelectedAttachment([]);
  };

  const pollJobStatus = async (jobId, onSuccess, onError) => {
    try {
      const response = await getJobStatus(jobId);

      if (response.status === "completed") {
        // Set all progress bars to 100% when job completes
        if (response.result.documents) {
          response.result.documents.forEach((doc) => {
            ["docx", "redline"].forEach((type) => {
              setAttachmentProgress((prev) => ({
                ...prev,
                [`${doc.file_id}_${type}`]: 100,
              }));
            });
          });
        }
        onSuccess(response.result);
      } else if (response.status === "error") {
        onError(response.error);
      } else {
        setTimeout(() => pollJobStatus(jobId, onSuccess, onError), 2000);
      }
    } catch (error) {
      onError(error);
    }
  };

  return (
    <Container size="md">
      <Title order={3} mb="xs" style={{ letterSpacing: "-1px" }}>
        Socius AI Associate
      </Title>
      <Text size="sm" mb="md">
        This is a demo of the Socius AI Associate, an email-based assistant for
        drafting.
      </Text>

      <Accordion
        variant="contained"
        value={activeAccordion}
        onChange={setActiveAccordion}
        multiple
      >
        <Transition
          mounted={emailTransitions.step1}
          transition="slide-right"
          duration={400}
          timingFunction="ease"
        >
          {(styles) => (
            <Accordion.Item value="step1" style={styles}>
              <Accordion.Control
                icon={
                  <Avatar color="var(--primary-color)" radius="xl">
                    AI
                  </Avatar>
                }
              >
                <Group>
                  <div>
                    <Text size="sm" fw={500}>
                      AI Legal Associate
                    </Text>
                    <Text size="xs" c="dimmed">
                      {ASSOCIATE_EMAIL}
                    </Text>
                  </div>
                  <Badge color="gray" variant="default">
                    Step 1: Send an email request
                  </Badge>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Paper shadow="xs" p="md" withBorder>
                  <Stack spacing="md">
                    {renderEmailContent(0)}
                    {currentStep === 0 && (
                      <Button
                        onClick={handleInitialEmail}
                        loading={loading}
                        loaderProps={{ type: "dots" }}
                        leftSection={<IconSend size={18} />}
                      >
                        Send Email
                      </Button>
                    )}
                  </Stack>
                </Paper>
              </Accordion.Panel>
            </Accordion.Item>
          )}
        </Transition>

        {currentStep >= 1 && (
          <Transition
            mounted={emailTransitions.step2}
            transition="slide-right"
            duration={400}
            timingFunction="ease"
          >
            {(styles) => (
              <Accordion.Item value="step2" style={styles}>
                <Accordion.Control
                  icon={
                    <Avatar color="var(--primary-color)" radius="xl">
                      AI
                    </Avatar>
                  }
                >
                  <Group>
                    <div>
                      <Text size="sm" fw={500}>
                        AI Legal Associate
                      </Text>
                      <Text size="xs" c="dimmed">
                        {ASSOCIATE_EMAIL}
                      </Text>
                    </div>
                    <Badge color="gray" variant="default">
                      Step 2: Receive a draft response
                    </Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Paper shadow="xs" p="md" withBorder>
                    <Stack spacing="md">{renderEmailContent(1)}</Stack>
                  </Paper>
                </Accordion.Panel>
              </Accordion.Item>
            )}
          </Transition>
        )}
      </Accordion>

      {currentStep > 0 && (
        <Button
          leftSection={<IconRefresh size={18} />}
          variant="outline"
          onClick={resetState}
          size="sm"
          mt="xl"
        >
          Start Over
        </Button>
      )}
    </Container>
  );
}

export default EmailDemo;
